import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Trash2, RefreshCw, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrashPage() {
  const [deletedClients, setDeletedClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeleted();
  }, []);

  const fetchDeleted = async () => {
    try {
      const res = await axios.get(`${API}/clients/trash`);
      setDeletedClients(res.data || []);
    } catch (err) {
      // If trash endpoint doesn't exist, show empty
      setDeletedClients([]);
    } finally {
      setLoading(false);
    }
  };

  const restoreClient = async (id) => {
    try {
      await axios.post(`${API}/clients/${id}/restore`);
      toast.success("Client restored");
      fetchDeleted();
    } catch (err) {
      toast.error("Failed to restore client");
    }
  };

  const permanentDelete = async (id) => {
    if (!window.confirm("Permanently delete this client? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/clients/${id}?permanent=true`);
      toast.success("Client permanently deleted");
      fetchDeleted();
    } catch (err) {
      toast.error("Failed to delete client");
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div></div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Trash</h1>
      <p className="text-zinc-400">Deleted clients can be restored or permanently removed</p>
      
      {deletedClients.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="py-12 text-center">
            <Trash2 className="h-16 w-16 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">Trash is empty</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {deletedClients.map((client) => (
            <Card key={client.id} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                    <User className="h-5 w-5 text-zinc-400" />
                  </div>
                  <div>
                    <p className="font-medium">{client.name}</p>
                    <p className="text-sm text-zinc-400">{client.email}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => restoreClient(client.id)}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Restore
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => permanentDelete(client.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
